import React, { useEffect, useState } from 'react';
import { ArrowRight, Calendar, Tag, BookOpen } from 'lucide-react';

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const apiBase = process.env.REACT_APP_API_BASE || '';

  useEffect(() => {
    fetch(apiBase + '/api/blogs')
      .then((r) => r.json())
      .then((data) => Array.isArray(data) ? setPosts(data) : setPosts([]))
      .catch(() => setPosts([]));
  }, [apiBase]);

  const formatContent = (content) => {
    if (!content) return '';

    const paragraphs = content.split('\n\n');
    const elements = [];
    let paraCount = 0;

    paragraphs.forEach((para, index) => {
      const trimmed = para.trim();
      if (!trimmed) return;

      // Check if it's a heading (ends with colon or starts with caps and short)
      const isHeading = (trimmed.endsWith(':') && trimmed.length < 80 && trimmed.split(' ').length <= 8) ||
                       (trimmed.match(/^[A-Z][^.!?]*:$/) && !trimmed.includes('\n'));

      if (isHeading) {
        elements.push(
          <div key={`heading-${index}`} className="mt-12 mb-8 first:mt-0">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <span className="text-white font-bold text-xl">{elements.filter(e => e.key?.startsWith('heading')).length + 1}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 leading-tight flex-1">
                {trimmed.replace(/:$/, '')}
              </h2>
            </div>
            <div className="h-1 w-32 bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 rounded-full ml-16"></div>
          </div>
        );
      } else {
        paraCount++;
        const formattedText = trimmed
          .replace(/\n/g, ' ')
          .replace(/\s+/g, ' ');

        // Add drop cap for first paragraph
        if (paraCount === 1) {
          const firstChar = formattedText.charAt(0);
          const restText = formattedText.slice(1);

          elements.push(
            <div key={`para-${index}`} className="mb-8">
              <p className="text-gray-800 leading-relaxed text-lg" style={{ lineHeight: '1.9' }}>
                <span className="float-left text-7xl font-bold text-transparent bg-gradient-to-br from-purple-600 to-indigo-600 bg-clip-text mr-3 mt-2 leading-none" style={{ lineHeight: '0.8' }}>
                  {firstChar}
                </span>
                <span className="text-gray-800">{restText}</span>
              </p>
            </div>
          );
        } else if (paraCount % 5 === 0) {
          // Add highlighted quote every 5 paragraphs
          elements.push(
            <div key={`para-${index}`} className="my-10 relative">
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-600 to-indigo-600 rounded-full"></div>
              <div className="bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 rounded-2xl p-8 border-2 border-purple-200 shadow-md">
                <svg className="w-10 h-10 text-purple-600 mb-4 opacity-50" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-xl text-gray-800 leading-relaxed font-medium italic">
                  {formattedText}
                </p>
              </div>
            </div>
          );
        } else if (paraCount % 3 === 0) {
          // Add emphasized paragraph every 3 paragraphs
          elements.push(
            <div key={`para-${index}`} className="my-8 pl-8 border-l-4 border-purple-400">
              <p className="text-gray-800 leading-relaxed text-lg font-medium" style={{ lineHeight: '1.9' }}>
                {formattedText}
              </p>
            </div>
          );
        } else {
          // Regular paragraph with enhanced styling
          elements.push(
            <p
              key={`para-${index}`}
              className="text-gray-700 leading-relaxed mb-8 text-lg"
              style={{ lineHeight: '1.9', textAlign: 'justify' }}
            >
              {formattedText}
            </p>
          );
        }
      }
    });

    return elements;
  };

  return (
    <div className="pt-20 py-16 bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-purple-600 font-semibold mb-3">
            <BookOpen className="w-5 h-5" />
            <span>Insights & Learnings</span>
          </div>
          <h1 className="text-5xl font-bold mb-4">Blog</h1>
          <div className="w-24 h-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 mx-auto rounded-full mb-6"></div>
        </div>

        {selectedPost && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fadeIn" onClick={() => setSelectedPost(null)}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-slideUp" onClick={(e) => e.stopPropagation()}>
              {/* Header with image */}
              <div className="relative h-80 bg-gradient-to-br from-purple-600 to-indigo-600 flex-shrink-0">
                {selectedPost.imageUrl && (
                  <img
                    src={selectedPost.imageUrl.startsWith('http') ? selectedPost.imageUrl : apiBase + selectedPost.imageUrl}
                    alt={selectedPost.title}
                    className="w-full h-full object-cover opacity-90"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <button
                  onClick={() => setSelectedPost(null)}
                  className="absolute top-6 right-6 bg-white/90 hover:bg-white rounded-full p-3 shadow-2xl transition-all hover:scale-110 hover:rotate-90"
                >
                  <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="flex items-center gap-3 text-sm text-white/95 mb-4">
                    <Calendar className="w-5 h-5" />
                    <span className="font-medium">{selectedPost.createdAt ? new Date(selectedPost.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</span>
                    <span className="mx-1">•</span>
                    <BookOpen className="w-5 h-5" />
                    <span className="font-medium">{Math.ceil(selectedPost.content.length / 1000)} min read</span>
                  </div>
                  <h2 className="text-5xl font-extrabold text-white mb-4 drop-shadow-2xl leading-tight">{selectedPost.title}</h2>
                  <div className="flex flex-wrap gap-2">
                    {(selectedPost.tags || []).map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5 bg-white/25 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-sm font-bold border-2 border-white/40 shadow-lg">
                        <Tag className="w-3.5 h-3.5" /> {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Content area */}
              <div className="overflow-y-auto flex-1 bg-gradient-to-b from-gray-50 via-white to-gray-50">
                <div className="max-w-3xl mx-auto px-10 py-12">
                  {selectedPost.excerpt && (
                    <div className="relative mb-12">
                      <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl blur opacity-20"></div>
                      <div className="relative bg-white rounded-2xl p-10 shadow-xl border border-purple-100">
                        <div className="flex items-start gap-4">
                          <svg className="w-12 h-12 text-purple-600 flex-shrink-0 opacity-40" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          <p className="text-2xl text-gray-800 leading-relaxed italic font-light flex-1">{selectedPost.excerpt}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <article className="prose prose-lg max-w-none">
                    {formatContent(selectedPost.content)}
                  </article>

                  {/* Decorative section divider */}
                  <div className="flex items-center justify-center my-12">
                    <div className="h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent w-full"></div>
                    <div className="px-6 flex gap-2">
                      <div className="w-2 h-2 rounded-full bg-purple-600 animate-pulse"></div>
                      <div className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 rounded-full bg-purple-600 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                    <div className="h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent w-full"></div>
                  </div>

                  {selectedPost.attachmentUrl && (
                    <div className="relative mt-10 mb-12">
                      <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur opacity-20"></div>
                      <div className="relative p-8 bg-white rounded-2xl border-2 border-blue-200 shadow-xl hover:shadow-2xl transition-all">
                        <div className="flex items-center gap-6">
                          <div className="relative w-16 h-16 flex-shrink-0">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl animate-pulse"></div>
                            <div className="relative w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="font-bold text-gray-900 text-xl mb-1">Download Attachment</div>
                            <div className="text-sm text-gray-600">{selectedPost.attachmentName || 'Supporting Document'}</div>
                          </div>
                          <a
                            href={apiBase + selectedPost.attachmentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-10 py-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl hover:scale-105"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Author section */}
                  <div className="relative mt-12 mb-8">
                    <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 rounded-2xl blur opacity-20"></div>
                    <div className="relative bg-white rounded-2xl p-8 shadow-xl border border-purple-100">
                      <div className="flex items-center gap-6">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full blur-md opacity-50"></div>
                          <img
                            src="/aneesh.jpg"
                            alt="Aneesh Kumar"
                            className="relative w-24 h-24 rounded-full border-4 border-white shadow-xl"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="font-bold text-gray-900 text-2xl">Aneesh Kumar</div>
                            <svg className="w-6 h-6 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="text-gray-600 text-base mb-2">Senior DevOps Engineer & Cloud Architect</div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-purple-600 font-bold flex items-center gap-2">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                              </svg>
                              13+ Years Experience
                            </span>
                            <span className="text-gray-500">•</span>
                            <span className="text-indigo-600 font-semibold">DevOps & Cloud Expert</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <article key={post.id} className="group bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all overflow-hidden border border-gray-100">
              <div className="relative h-56 overflow-hidden">
                {post.imageUrl && <img src={post.imageUrl.startsWith('http') ? post.imageUrl : apiBase + post.imageUrl} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white flex items-center gap-2 text-xs">
                  <Calendar className="w-4 h-4" />
                  <span>{post.createdAt ? new Date(post.createdAt).toLocaleDateString() : ''}</span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2 text-gray-900">{post.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed text-sm">{post.excerpt || (post.content ? post.content.slice(0, 140) + '...' : '')}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(post.tags || []).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 px-2.5 py-1 rounded-lg text-xs font-semibold border border-purple-100">
                      <Tag className="w-3.5 h-3.5" /> {t}
                    </span>
                  ))}
                </div>
                {post.attachmentUrl && (
                  <a href={apiBase + post.attachmentUrl} target="_blank" rel="noreferrer" className="text-blue-600 font-semibold mr-4 underline">
                    {post.attachmentName || 'Download'}
                  </a>
                )}
                <button onClick={() => setSelectedPost(post)} className="text-purple-600 font-semibold hover:gap-3 transition-all inline-flex items-center gap-2">
                  Read More <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </article>
          ))}
          {!posts.length && (
            <div className="text-gray-500">No posts yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}


